import React, { useState, useEffect } from "react";
import Card from "../../components/common/card/Card";
import InputField from "../../components/common/inputs/InputField";
import PrimaryButton from "../../components/common/buttons/PrimaryButton";
import { CircleX, Loader2 } from "lucide-react";
import {
  saveCorporationCode,
  searchRandomFlight,
  getAllCorporation,
} from "./negotiatePriceController";
import { getAirLineName } from "../../utils/airLineNameByCode";
import { CorporationCode, ToastData } from "../../types/types";
import DeleteButton from "../../components/common/buttons/DeleteButton";
import Toast from "../../components/common/toast/Toast";

const NegotiatePrice = () => {
  const [airlineCode, setAirlineCode] = useState<string>("");
  const [offerCode, setOfferCode] = useState<string>("");
  const [isTestLoading, setIsTestLoading] = useState<boolean>(false);
  const [isApplyLoading, setIsApplyLoading] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<boolean>(false);
  const [appliedCodes, setAppliedCodes] = useState<CorporationCode[]>([]);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [openAddNewCode, setOpenAddNewCode] = useState<boolean>(true);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const loadCorporateCodes = async () => {
      try {
        const codes = await getAllCorporation();
        setAppliedCodes(codes);
      } catch (err) {
        console.error("Failed to load corporation codes:", err);
      }
    };

    loadCorporateCodes();
  }, []);

  const handleTestCode = async () => {
    setIsTestLoading(true);

    if (!airlineCode || !offerCode) {
      setError("Please enter both airline code and offer code");
      setIsTestLoading(false);
      return;
    }

    setError("");

    try {
      const resultBool = await searchRandomFlight(offerCode, airlineCode);
      if (resultBool) {
        setTestResult(true);
        setToast({
          showToast: true,
          message: "Code Test successfully",
        });
        setIsTestLoading(false);
      } else {
        setToast({
          type: "error",
          showToast: true,
          message: "Invalid Code",
        });
        setIsTestLoading(false);
      }
    } catch (err) {
      setError("Failed to test code. Please try again.");
      setToast({
        showToast: true,
        type: "error",
        message: "Failed to test code. Please try again.",
      });
    }
  };

  const handleApplyCode = async () => {
    if (!testResult) {
      setError("Please test the code first");
      return;
    }

    setError("");
    setIsApplyLoading(true);

    try {
      if (airlineCode && offerCode) {
        await saveCorporationCode(offerCode, airlineCode);
        const updatedCodes = await getAllCorporation();
        setAppliedCodes(updatedCodes);
      }

      setAirlineCode("");
      setOfferCode("");
      setTestResult(false);
    } catch (err) {
      setToast({
        showToast: true,
        type: "error",
        message: "Failed to apply code. Please try again.",
      });
      setError("Failed to apply code. Please try again.");
    } finally {
      setToast({
        showToast: true,
        type: "success",
        message: "Code applied successfully",
      });
      setIsApplyLoading(false);
    }
  };

  const toggleAddNewCode = () => {
    setOpenAddNewCode((prev) => !prev);
    setTestResult(false);
    setError("");
    setAirlineCode("");
    setOfferCode("");
  };

  const closeToast = () => {
    setToast(null);
  };

  useEffect(() => {
    if (toast?.showToast) {
      setTimeout(() => {
        closeToast();
      }, 3000);
    }
  }, [setToast, toast]);

  const handleDeleteCode = (id: number) => {
    setAppliedCodes((prevCodes) => prevCodes.filter((code) => code.id !== id));
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {toast?.showToast && (
        <Toast
          type={toast.type ?? "success"}
          message={toast.message ?? ""}
          onClose={() => {}}
        ></Toast>
      )}
      <h2 className="text-3xl font-bold">Negotiate Price</h2>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold mb-9">Price Code Add And Test</h2>
          {openAddNewCode ? (
            <div className="">
              <PrimaryButton onClick={toggleAddNewCode}>
                New Corporate Code
              </PrimaryButton>
            </div>
          ) : (
            <div onClick={toggleAddNewCode} className="cursor-pointer">
              <CircleX />
            </div>
          )}
        </div>
        <div
          className={`space-y-4 transition-all ease-in-out duration-200 ${
            openAddNewCode ? "opacity-0 max-h-0" : "opacity-100 max-h-screen"
          } overflow-hidden`}
        >
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
          <InputField
            onChange={(e) => {
              if (e.length < 3) {
                setAirlineCode(e.toUpperCase());
                const airLineName = getAirLineName(e.toUpperCase());
                if (airLineName !== "N/A") {
                  setAirlineCode(e.toUpperCase() + `(${airLineName})`);
                }
              }
            }}
            placeholder="Airline Code"
            value={airlineCode}
            //   onChange={(e) => setAirlineCode(e.target.value)}
          />
          <InputField
            placeholder="Corporation Code"
            value={offerCode}
            onChange={(e) => setOfferCode(e)}
          />
          {/* </div> */}

          <div className="flex gap-4">
            <div className="w-32">
              <PrimaryButton
                onClick={handleTestCode}
                disabled={isTestLoading}
                //   className="w-32"
              >
                {isTestLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Test Code"
                )}
              </PrimaryButton>
            </div>
            <div className="w-32">
              <PrimaryButton
                onClick={handleApplyCode}
                disabled={!testResult || isApplyLoading}
                //   className="w-32"
              >
                {isApplyLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Apply Code"
                )}
              </PrimaryButton>
            </div>
          </div>
          <div>test steps</div>
          {/* {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )} */}

          {/* {testResult && ( */}
        </div>
      </Card>

      <Card>
        <h2 className="text-xl font-bold mb-9">Applied Price Codes</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Airline Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Offer Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applied Date
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {appliedCodes.map((code) => (
                <tr key={code.id}>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    {code.airLineCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {code.corporationCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {new Date(code.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <DeleteButton
                      onConfirm={() => handleDeleteCode(code.id)}
                    ></DeleteButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default NegotiatePrice;
